import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { Button } from '../ui/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { 
  Calendar,
  Video,
  Clock,
  FileText,
  Pill,
  Phone,
  User,
  Heart,
  AlertCircle,
  CheckCircle,
  MapPin,
  Star
} from 'lucide-react';

// Mock data for demonstration
const mockAppointments = [
  {
    id: '1',
    date: '2024-02-15',
    time: '10:00 AM',
    provider: 'Dr. Sarah Smith',
    specialty: 'General Psychiatry',
    type: 'telehealth',
    status: 'upcoming',
    duration: 60
  },
  {
    id: '2',
    date: '2024-02-08',
    time: '2:30 PM',
    provider: 'Dr. Michael Johnson',
    specialty: 'Addiction Psychiatry',
    type: 'telehealth',
    status: 'completed',
    duration: 45
  },
  {
    id: '3',
    date: '2024-01-28',
    time: '11:15 AM',
    provider: 'Dr. Sarah Smith',
    specialty: 'General Psychiatry',
    type: 'telehealth',
    status: 'completed',
    duration: 60
  }
];

const mockMedications = [
  {
    name: 'Sertraline',
    dosage: '50mg',
    frequency: 'Once daily',
    prescriber: 'Dr. Sarah Smith',
    lastRefill: '2024-02-01'
  },
  {
    name: 'Lorazepam',
    dosage: '0.5mg',
    frequency: 'As needed',
    prescriber: 'Dr. Sarah Smith',
    lastRefill: '2024-02-01'
  }
];

const mockHealthMetrics = {
  lastVisit: '2024-02-08',
  nextVisit: '2024-02-15',
  totalVisits: 12,
  adherenceScore: 92
};

export const PatientDashboard = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');

  const upcomingAppointments = mockAppointments.filter(apt => apt.status === 'upcoming');
  const nextAppointment = upcomingAppointments[0];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'upcoming': return 'bg-blue-100 text-blue-800';
      case 'completed': return 'bg-green-100 text-green-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl text-gray-900">Welcome back, {user?.name?.split(' ')[0]}!</h1>
          <p className="text-gray-600 mt-2">Manage your mental health journey with personalized care.</p>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Calendar className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Next Appointment</p>
                  <p className="text-lg text-gray-900">
                    {nextAppointment ? 'Feb 15' : 'None scheduled'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                  <CheckCircle className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Total Visits</p>
                  <p className="text-lg text-gray-900">{mockHealthMetrics.totalVisits}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Heart className="h-6 w-6 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Adherence Score</p>
                  <p className="text-lg text-gray-900">{mockHealthMetrics.adherenceScore}%</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
                  <Pill className="h-6 w-6 text-yellow-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Active Medications</p>
                  <p className="text-lg text-gray-900">{mockMedications.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="appointments">Appointments</TabsTrigger>
            <TabsTrigger value="medications">Medications</TabsTrigger>
            <TabsTrigger value="records">Health Records</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-6">
            <div className="grid lg:grid-cols-3 gap-6">
              {/* Upcoming Appointments */}
              <div className="lg:col-span-2">
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center space-x-2">
                        <Calendar className="h-5 w-5" />
                        <span>Upcoming Appointments</span>
                      </CardTitle>
                      <CardDescription>Your scheduled consultations</CardDescription>
                    </div>
                    <Button asChild>
                      <Link to="/booking/telehealth">Book New</Link>
                    </Button>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {upcomingAppointments.length > 0 ? (
                      upcomingAppointments.map((appointment) => (
                        <div key={appointment.id} className="border border-gray-200 rounded-lg p-4">
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center space-x-3">
                              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                                <Video className="h-5 w-5 text-blue-600" />
                              </div>
                              <div>
                                <h4 className="text-gray-900">{appointment.provider}</h4>
                                <p className="text-sm text-gray-600">{appointment.specialty}</p>
                              </div>
                            </div>
                            <Badge className={getStatusColor(appointment.status)}>
                              {appointment.status}
                            </Badge>
                          </div>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-4 text-sm text-gray-600">
                              <span className="flex items-center space-x-1">
                                <Calendar className="h-4 w-4" />
                                <span>{formatDate(appointment.date)}</span>
                              </span>
                              <span className="flex items-center space-x-1">
                                <Clock className="h-4 w-4" />
                                <span>{appointment.time}</span>
                              </span>
                            </div>
                            <Button size="sm" asChild>
                              <Link to={`/consultation/${appointment.id}`}>Join Call</Link>
                            </Button>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-8">
                        <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                        <p className="text-gray-600 mb-4">No upcoming appointments</p>
                        <Button asChild>
                          <Link to="/booking/telehealth">Schedule your first consultation</Link>
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Quick Actions */}
              <div className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Quick Actions</CardTitle>
                    <CardDescription>Common tasks and shortcuts</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <Button className="w-full justify-start" asChild>
                      <Link to="/booking/telehealth">
                        <Video className="mr-2 h-4 w-4" />
                        Book Telehealth
                      </Link>
                    </Button>
                    <Button variant="outline" className="w-full justify-start" asChild>
                      <Link to="/providers">
                        <User className="mr-2 h-4 w-4" />
                        Find Provider
                      </Link>
                    </Button>
                    <Button variant="outline" className="w-full justify-start">
                      <Phone className="mr-2 h-4 w-4" />
                      Crisis Support
                    </Button>
                    <Button variant="outline" className="w-full justify-start">
                      <FileText className="mr-2 h-4 w-4" />
                      View Records
                    </Button>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center space-x-2">
                      <AlertCircle className="h-5 w-5 text-yellow-500" />
                      <span>Reminders</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <p className="text-sm text-yellow-800">Take your morning medication</p>
                      <p className="text-xs text-yellow-600 mt-1">Sertraline 50mg</p>
                    </div>
                    <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <p className="text-sm text-blue-800">Appointment tomorrow at 10:00 AM</p>
                      <p className="text-xs text-blue-600 mt-1">Dr. Sarah Smith - Telehealth</p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="appointments" className="space-y-6">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>All Appointments</CardTitle>
                  <CardDescription>View your appointment history and upcoming visits</CardDescription>
                </div>
                <Button asChild>
                  <Link to="/booking/telehealth">Book New Appointment</Link>
                </Button>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {mockAppointments.map((appointment) => (
                    <div key={appointment.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center space-x-3">
                          <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                            <Video className="h-6 w-6 text-blue-600" />
                          </div>
                          <div>
                            <h4 className="text-gray-900">{appointment.provider}</h4>
                            <p className="text-sm text-gray-600">{appointment.specialty}</p>
                          </div>
                        </div>
                        <Badge className={getStatusColor(appointment.status)}>
                          {appointment.status}
                        </Badge>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-6 text-sm text-gray-600">
                          <span className="flex items-center space-x-1">
                            <Calendar className="h-4 w-4" />
                            <span>{formatDate(appointment.date)}</span>
                          </span>
                          <span className="flex items-center space-x-1">
                            <Clock className="h-4 w-4" />
                            <span>{appointment.time}</span>
                          </span>
                          <span>{appointment.duration} min</span>
                        </div>
                        {appointment.status === 'upcoming' && (
                          <div className="flex space-x-2">
                            <Button size="sm" variant="outline">Reschedule</Button>
                            <Button size="sm" asChild>
                              <Link to={`/consultation/${appointment.id}`}>Join Call</Link>
                            </Button>
                          </div>
                        )}
                        {appointment.status === 'completed' && (
                          <Button size="sm" variant="outline">
                            View Notes
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="medications" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Pill className="h-5 w-5" />
                  <span>Current Medications</span>
                </CardTitle>
                <CardDescription>Manage your prescribed medications</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {mockMedications.map((medication, index) => (
                    <div key={index} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-gray-900">{medication.name}</h4>
                        <Button size="sm" variant="outline">Request Refill</Button>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-gray-600">
                        <div>
                          <span className="block">Dosage</span>
                          <span className="text-gray-900">{medication.dosage}</span>
                        </div>
                        <div>
                          <span className="block">Frequency</span>
                          <span className="text-gray-900">{medication.frequency}</span>
                        </div>
                        <div>
                          <span className="block">Prescriber</span>
                          <span className="text-gray-900">{medication.prescriber}</span>
                        </div>
                        <div>
                          <span className="block">Last Refill</span>
                          <span className="text-gray-900">{medication.lastRefill}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="records" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <FileText className="h-5 w-5" />
                  <span>Health Records</span>
                </CardTitle>
                <CardDescription>Access your medical records and documents</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="text-gray-900">Psychiatric Evaluation Report</h4>
                        <p className="text-sm text-gray-600">Dr. Sarah Smith • February 8, 2024</p>
                      </div>
                      <Button size="sm" variant="outline">
                        <FileText className="mr-2 h-4 w-4" />
                        View
                      </Button>
                    </div>
                  </div>
                  <div className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="text-gray-900">Medication History</h4>
                        <p className="text-sm text-gray-600">Complete medication timeline</p>
                      </div>
                      <Button size="sm" variant="outline">
                        <FileText className="mr-2 h-4 w-4" />
                        View
                      </Button>
                    </div>
                  </div>
                  <div className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="text-gray-900">Lab Results</h4>
                        <p className="text-sm text-gray-600">Blood work from January 15, 2024</p>
                      </div>
                      <Button size="sm" variant="outline">
                        <FileText className="mr-2 h-4 w-4" />
                        View
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};